import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Button,
  IconButton,
  Avatar,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  alpha,
  TextField,
  Switch,
  FormControlLabel,
  Tooltip,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Snackbar,
  Collapse,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Business as BusinessIcon,
  DirectionsCar as VehicleIcon,
  Receipt as ReceiptIcon,
  Devices as DevicesIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  VpnKey as PasswordIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  Payment as PaymentIcon,
  PictureAsPdf as PdfIcon,
  Folder as FolderIcon,
  AccountBalance as CheckIcon,
} from '@mui/icons-material';
import { clientsAPI, documentsAPI, clientInformationAPI, informationTypesAPI, checksAPI } from '../services/api';
import PaymentModal from '../components/PaymentModal';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

const ClientProfilePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [newPassword, setNewPassword] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });
  const [infoExpanded, setInfoExpanded] = useState(true);
  const [paymentModal, setPaymentModal] = useState({ open: false, document: null });
  const [clientInfo, setClientInfo] = useState([]);
  const [clientChecks, setClientChecks] = useState([]);
  const [infoEditing, setInfoEditing] = useState(false);
  const [infoSaving, setInfoSaving] = useState(false);
  
  // Add Information Dialog states
  const [addInfoDialog, setAddInfoDialog] = useState(false);
  const [availableInfoTypes, setAvailableInfoTypes] = useState([]);
  const [selectedInfoType, setSelectedInfoType] = useState('');
  const [newInfoValue, setNewInfoValue] = useState('');
  const [showInDocuments, setShowInDocuments] = useState(true);
  const [addInfoSaving, setAddInfoSaving] = useState(false);

  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const profileRes = await clientsAPI.getProfile(id);
      setProfile(profileRes.data);
      
      // Try to fetch client information (may fail if no info types exist)
      try {
        const infoRes = await clientInformationAPI.getAll({ client: id });
        setClientInfo(infoRes.data?.results || infoRes.data || []);
      } catch (infoErr) {
        console.warn('Could not fetch client information:', infoErr);
        setClientInfo([]);
      }
      
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement du profil client');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const handleResetPassword = async () => {
    try {
      setResetting(true);
      const response = await clientsAPI.resetPassword(id);
      setNewPassword(response.data);
      setPasswordDialog(true);
    } catch (err) {
      setError('Erreur lors de la réinitialisation du mot de passe');
    } finally {
      setResetting(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setSnackbar({ open: true, message: 'Copié!' });
  };

  const handleEditStart = () => {
    setEditData({
      company_name: profile?.company_name || '',
      contact_person: profile?.contact_person || '',
      email: profile?.email || '',
      phone: profile?.phone || '',
      fax: profile?.fax || '',
      address: profile?.address || '',
      city: profile?.city || '',
      postal_code: profile?.postal_code || '',
      rc_number: profile?.rc_number || '',
      rc_issue_date: profile?.rc_issue_date || '',
      nif_number: profile?.nif_number || '',
      nis_number: profile?.nis_number || '',
      ai_number: profile?.ai_number || '',
      is_active: profile?.is_active ?? true,
    });
    setEditMode(true);
    setInfoExpanded(true);
  };

  const handleEditCancel = () => {
    setEditMode(false);
    setEditData({});
  };

  const handleEditSave = async () => {
    try {
      setSaving(true);
      await clientsAPI.update(id, editData);
      setSnackbar({ open: true, message: 'Client mis à jour avec succès!' });
      setEditMode(false);
      fetchProfile();
    } catch (err) {
      console.error('Error updating client:', err);
      setSnackbar({ open: true, message: 'Erreur lors de la mise à jour' });
    } finally {
      setSaving(false);
    }
  };

  const handleDownloadPDF = async (docId) => {
    try {
      const response = await documentsAPI.download(docId);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      setSnackbar({ open: true, message: 'Erreur lors du téléchargement' });
    }
  };

  const handleInfoChange = (index, value) => {
    const updated = [...clientInfo];
    updated[index] = { ...updated[index], value };
    setClientInfo(updated);
  };

  const handleStartInfoEdit = () => {
    setInfoEditing(true);
  };

  const handleCancelInfoEdit = () => {
    setInfoEditing(false);
    fetchProfile(); // Reset to original values
  };

  const handleSaveInfo = async () => {
    try {
      setInfoSaving(true);
      const requestData = {
        information: clientInfo.map(i => ({ 
          information_type: i.information_type, 
          value: i.value || '',
          show_in_documents: i.show_in_documents
        }))
      };
      
      // Add client_id for admin users
      if (user?.is_staff) {
        requestData.client_id = id;
      }
      
      console.log('DEBUG: Sending bulk update data:', requestData);
      await clientInformationAPI.bulkUpdate(requestData);
      setSnackbar({ open: true, message: 'Informations client mises à jour!' });
      setInfoEditing(false);
    } catch (err) {
      console.error('Error updating client information:', err);
      console.error('Error response:', err.response?.data);
      setSnackbar({ open: true, message: 'Erreur lors de la mise à jour' });
    } finally {
      setInfoSaving(false);
    }
  };

  const loadChecks = useCallback(async () => {
    try {
      const response = await checksAPI.getAll({ client: id });
      setClientChecks(response.data.results || response.data || []);
    } catch (err) {
      console.error('Error loading checks:', err);
    }
  }, [id]);

  // Add Information Dialog handlers
  const handleOpenAddInfoDialog = async () => {
    try {
      const response = await informationTypesAPI.getActive();
      const types = response.data?.results || response.data || [];
      // Filter out already existing types for this client
      const existingTypeIds = clientInfo.map(i => i.information_type);
      const availableTypes = types.filter(t => !existingTypeIds.includes(t.id));
      setAvailableInfoTypes(availableTypes);
      setAddInfoDialog(true);
      setSelectedInfoType('');
      setNewInfoValue('');
      setShowInDocuments(true);
    } catch (err) {
      console.error('Error loading information types:', err);
      setSnackbar({ open: true, message: 'Erreur lors du chargement des types d\'information' });
    }
  };

  const handleCloseAddInfoDialog = () => {
    setAddInfoDialog(false);
    setSelectedInfoType('');
    setNewInfoValue('');
    setShowInDocuments(true);
  };

  const handleSaveNewInfo = async () => {
    if (!selectedInfoType) {
      setSnackbar({ open: true, message: 'Veuillez sélectionner un type d\'information' });
      return;
    }
    try {
      setAddInfoSaving(true);
      await clientInformationAPI.create({
        client: parseInt(id),
        information_type: parseInt(selectedInfoType),
        value: newInfoValue,
        show_in_documents: showInDocuments,
      });
      setSnackbar({ open: true, message: 'Information ajoutée avec succès!' });
      setAddInfoDialog(false);
      // Refresh the client info list
      fetchProfile();
    } catch (err) {
      console.error('Error adding information:', err);
      setSnackbar({ open: true, message: 'Erreur lors de l\'ajout de l\'information' });
    } finally {
      setAddInfoSaving(false);
    }
  };

  const getSelectedTypeFieldType = () => {
    const selected = availableInfoTypes.find(t => t.id === parseInt(selectedInfoType));
    return selected?.field_type || 'text';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/clients')} sx={{ mb: 2 }}>
          Retour aux clients
        </Button>
        
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: '1.5rem', mr: 2 }}>
              {profile?.company_name?.[0]?.toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{profile?.company_name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {profile?.client_code} • Créé le {new Date(profile?.created_at).toLocaleDateString('fr-FR')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {editMode ? (
              <>
                <Button variant="contained" color="success" startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />} onClick={handleEditSave} disabled={saving}>
                  Enregistrer
                </Button>
                <Button variant="outlined" startIcon={<CloseIcon />} onClick={handleEditCancel} disabled={saving}>
                  Annuler
                </Button>
              </>
            ) : (
              <>
                <Button variant="contained" startIcon={<EditIcon />} onClick={handleEditStart}>Modifier</Button>
                <Button variant="outlined" startIcon={<PasswordIcon />} onClick={handleResetPassword} disabled={resetting}>
                  Réinitialiser MDP
                </Button>
                <IconButton onClick={fetchProfile}><RefreshIcon /></IconButton>
              </>
            )}
          </Box>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        {[
          { label: 'Véhicules', value: profile?.total_vehicles, icon: VehicleIcon, color: '#6366f1' },
          { label: 'Dispositifs', value: profile?.total_devices, icon: DevicesIcon, color: '#f59e0b' },
          { label: 'Factures', value: profile?.total_invoices, icon: ReceiptIcon, color: '#ec4899' },
        ].map((stat) => (
          <Box key={stat.label} sx={{ flex: '1 1 calc(33.333% - 11px)', minWidth: 150 }}>
            <Card>
              <CardContent sx={{ py: 2, px: 2, '&:last-child': { pb: 2 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700, color: stat.color }}>{stat.value || 0}</Typography>
                    <Typography variant="caption" color="text.secondary">{stat.label}</Typography>
                  </Box>
                  <Box sx={{ p: 1, borderRadius: 2, bgcolor: alpha(stat.color, 0.1) }}>
                    <stat.icon sx={{ color: stat.color, fontSize: 24 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>
        ))}
      </Box>

      {/* Client Info - Collapsible */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => !editMode && setInfoExpanded(!infoExpanded)}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>Informations du Client</Typography>
            {!editMode && (
              <IconButton size="small">{infoExpanded ? <CollapseIcon /> : <ExpandIcon />}</IconButton>
            )}
          </Box>
        </CardContent>
        <Collapse in={infoExpanded}>
          <CardContent sx={{ pt: 0 }}>
            {editMode ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
                    <TextField label="Nom de l'entreprise" size="small" fullWidth value={editData.company_name} onChange={(e) => setEditData({ ...editData, company_name: e.target.value })} />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
                    <TextField label="Contact" size="small" fullWidth value={editData.contact_person} onChange={(e) => setEditData({ ...editData, contact_person: e.target.value })} />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
                    <TextField label="Email" size="small" fullWidth type="email" value={editData.email} onChange={(e) => setEditData({ ...editData, email: e.target.value })} />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
                    <TextField label="Téléphone" size="small" fullWidth value={editData.phone} onChange={(e) => setEditData({ ...editData, phone: e.target.value })} />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 180 }}>
                    <TextField label="Fax" size="small" fullWidth value={editData.fax} onChange={(e) => setEditData({ ...editData, fax: e.target.value })} />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: 200 }}>
                    <TextField label="Adresse" size="small" fullWidth value={editData.address} onChange={(e) => setEditData({ ...editData, address: e.target.value })} />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 150 }}>
                    <TextField label="Ville" size="small" fullWidth value={editData.city} onChange={(e) => setEditData({ ...editData, city: e.target.value })} />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 150 }}>
                    <TextField label="Code postal" size="small" fullWidth value={editData.postal_code} onChange={(e) => setEditData({ ...editData, postal_code: e.target.value })} />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 150, display: 'flex', alignItems: 'center' }}>
                    <FormControlLabel control={<Switch checked={editData.is_active} onChange={(e) => setEditData({ ...editData, is_active: e.target.checked })} />} label="Compte actif" />
                  </Box>
                </Box>
                <Divider><Typography variant="caption">Identifiants Fiscaux</Typography></Divider>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 140 }}>
                    <TextField label="RC N°" size="small" fullWidth value={editData.rc_number} onChange={(e) => setEditData({ ...editData, rc_number: e.target.value })} />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 140 }}>
                    <TextField label="Date de délivrance RC" size="small" fullWidth type="date" InputLabelProps={{ shrink: true }} value={editData.rc_issue_date} onChange={(e) => setEditData({ ...editData, rc_issue_date: e.target.value })} />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 140 }}>
                    <TextField label="NIF N°" size="small" fullWidth value={editData.nif_number} onChange={(e) => setEditData({ ...editData, nif_number: e.target.value })} />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 140 }}>
                    <TextField label="NIS N°" size="small" fullWidth value={editData.nis_number} onChange={(e) => setEditData({ ...editData, nis_number: e.target.value })} />
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 12px)', minWidth: 140 }}>
                    <TextField label="AI N°" size="small" fullWidth value={editData.ai_number} onChange={(e) => setEditData({ ...editData, ai_number: e.target.value })} />
                  </Box>
                </Box>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <BusinessIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary">Contact</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile?.contact_person || '-'}</Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <EmailIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile?.email || '-'}</Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <PhoneIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary">Téléphone</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile?.phone || '-'}</Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 calc(25% - 18px)', minWidth: 150 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
                      <Typography variant="caption" color="text.secondary">Adresse</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile?.address || '-'}, {profile?.city || ''}</Typography>
                  </Box>
                </Box>
                <Divider />
                {/* ARPCE Fields Row */}
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ minWidth: 100 }}>
                    <Typography variant="caption" color="text.secondary">Code Postal</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile?.postal_code || '-'}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 100 }}>
                    <Typography variant="caption" color="text.secondary">Fax</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile?.fax || '-'}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 120 }}>
                    <Typography variant="caption" color="text.secondary">Date délivrance RC</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile?.rc_issue_date || '-'}</Typography>
                  </Box>
                </Box>
                <Divider />
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ minWidth: 100 }}>
                    <Typography variant="caption" color="text.secondary">RC N°</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile?.rc_number || '-'}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 100 }}>
                    <Typography variant="caption" color="text.secondary">NIF N°</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile?.nif_number || '-'}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 100 }}>
                    <Typography variant="caption" color="text.secondary">NIS N°</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile?.nis_number || '-'}</Typography>
                  </Box>
                  <Box sx={{ minWidth: 100 }}>
                    <Typography variant="caption" color="text.secondary">AI N°</Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{profile?.ai_number || '-'}</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 150 }}>
                    <Typography variant="caption" color="text.secondary">Compte Utilisateur</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>@{profile?.username}</Typography>
                      <Chip label={profile?.is_active ? 'Actif' : 'Inactif'} color={profile?.is_active ? 'success' : 'error'} size="small" />
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
          </CardContent>
        </Collapse>
      </Card>

      {/* Full Width Tabs */}
      <Card>
        <Tabs value={tabValue} onChange={(e, v) => { setTabValue(v); if (v === 4) loadChecks(); }} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab label="Véhicules" icon={<VehicleIcon />} iconPosition="start" />
          <Tab label="Documents" icon={<ReceiptIcon />} iconPosition="start" />
          <Tab label="Fichiers Client" icon={<FolderIcon />} iconPosition="start" />
          <Tab label="Informations" icon={<BusinessIcon />} iconPosition="start" />
          <Tab label="Chèques" icon={<CheckIcon />} iconPosition="start" />
        </Tabs>

        {/* Vehicles Tab */}
        <TabPanel value={tabValue} index={0}>
          <TableContainer sx={{ px: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Véhicule</strong></TableCell>
                  <TableCell><strong>Immatriculation</strong></TableCell>
                  <TableCell><strong>Marque/Modèle</strong></TableCell>
                  <TableCell><strong>IMEI GPS</strong></TableCell>
                  <TableCell><strong>Statut</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profile?.vehicles?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Aucun véhicule</Typography>
                      <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => navigate('/vehicles')}>Ajouter</Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  profile?.vehicles?.map((v) => (
                    <TableRow key={v.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{v.name || '-'}</TableCell>
                      <TableCell><Chip label={v.registration_number || '-'} size="small" variant="outlined" /></TableCell>
                      <TableCell>{v.brand} {v.model}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace' }}>{v.device?.imei || '-'}</TableCell>
                      <TableCell>
                        <Chip icon={v.device?.is_active ? <ActiveIcon /> : <InactiveIcon />} label={v.device?.is_active ? 'Actif' : 'Inactif'} color={v.device?.is_active ? 'success' : 'default'} size="small" />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Documents Tab */}
        <TabPanel value={tabValue} index={1}>
          <TableContainer sx={{ px: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>N° Document</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Montant TTC</strong></TableCell>
                  <TableCell><strong>Statut</strong></TableCell>
                  <TableCell><strong>Paiement</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {profile?.recent_documents?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Aucun document</Typography>
                      <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => navigate('/documents')}>Créer</Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  profile?.recent_documents?.map((d) => (
                    <TableRow key={d.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{d.document_number}</TableCell>
                      <TableCell>
                        <Chip label={d.document_type_display} size="small" color={d.document_type === 'invoice' ? 'success' : d.document_type === 'proforma' ? 'warning' : 'info'} />
                      </TableCell>
                      <TableCell>{d.document_date}</TableCell>
                      <TableCell>{d.total_ttc ? `${parseFloat(d.total_ttc).toLocaleString()} DZD` : '-'}</TableCell>
                      <TableCell>
                        <Chip label={d.status_display} size="small" variant="outlined" color={d.status === 'validated' ? 'success' : d.status === 'pending_client' ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell>
                        {d.invoice_details && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Chip 
                              label={d.invoice_details.payment_status_display} 
                              size="small" 
                              color={d.invoice_details.payment_status === 'paid' ? 'success' : d.invoice_details.payment_status === 'partial' ? 'warning' : 'default'}
                            />
                            {['validated', 'client_accepted'].includes(d.status) && (
                              <Tooltip title="Gérer paiements">
                                <IconButton size="small" color="success" onClick={() => setPaymentModal({ open: true, document: d })}>
                                  <PaymentIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {d.pdf_file && (
                          <Tooltip title="Télécharger PDF">
                            <IconButton size="small" onClick={() => handleDownloadPDF(d.id)}><DownloadIcon fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Client Documents Tab */}
        <TabPanel value={tabValue} index={2}>
          <TableContainer sx={{ px: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Nom</strong></TableCell>
                  <TableCell><strong>Type</strong></TableCell>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Ajouté par</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(!profile?.client_documents || profile?.client_documents?.length === 0) ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Aucun fichier</Typography>
                      <Button size="small" startIcon={<AddIcon />} sx={{ mt: 1 }} onClick={() => navigate('/client-documents')}>Ajouter</Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  profile?.client_documents?.map((doc) => (
                    <TableRow key={doc.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PdfIcon color="error" fontSize="small" />
                          <Typography variant="body2" fontWeight={500}>{doc.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={doc.document_type || '-'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {doc.created_at ? new Date(doc.created_at).toLocaleDateString('fr-FR') : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {doc.uploaded_by || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {doc.file_url && (
                          <Tooltip title="Télécharger PDF">
                            <IconButton size="small" onClick={() => window.open(doc.file_url, '_blank')}>
                              <DownloadIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Client Information Tab */}
        <TabPanel value={tabValue} index={3}>
          <Box sx={{ px: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Informations Supplémentaires</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {user?.is_staff && !infoEditing && (
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAddInfoDialog}>
                    Ajouter une information
                  </Button>
                )}
                {clientInfo.length > 0 && (
                  infoEditing ? (
                    <>
                      <Button variant="contained" color="success" startIcon={infoSaving ? <CircularProgress size={16} /> : <SaveIcon />} onClick={handleSaveInfo} disabled={infoSaving}>
                        Enregistrer
                      </Button>
                      <Button variant="outlined" startIcon={<CloseIcon />} onClick={handleCancelInfoEdit} disabled={infoSaving}>
                        Annuler
                      </Button>
                    </>
                  ) : (
                    <Button variant="contained" startIcon={<EditIcon />} onClick={handleStartInfoEdit}>Modifier</Button>
                  )
                )}
              </Box>
            </Box>
            
            {clientInfo.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                  Aucune information supplémentaire définie
                </Typography>
                <Button 
                  variant="outlined" 
                  component="a" 
                  href="/information-types" 
                  startIcon={<AddIcon />}
                >
                  Gérer les types d'informations
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Information</strong></TableCell>
                      <TableCell><strong>Type</strong></TableCell>
                      <TableCell><strong>Valeur</strong></TableCell>
                      <TableCell><strong>Position</strong></TableCell>
                      <TableCell><strong>Visible</strong></TableCell>
                      {infoEditing && <TableCell><strong>Obligatoire</strong></TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {clientInfo.map((info, index) => (
                      <TableRow key={info.id || info.information_type} hover>
                        <TableCell sx={{ fontWeight: 500 }}>{info.information_type_name}</TableCell>
                        <TableCell>
                          <Chip 
                            label={info.field_type === 'text' ? 'Texte' :
                                   info.field_type === 'number' ? 'Nombre' :
                                   info.field_type === 'email' ? 'Email' :
                                   info.field_type === 'phone' ? 'Téléphone' :
                                   info.field_type === 'date' ? 'Date' :
                                   info.field_type === 'textarea' ? 'Texte long' : info.field_type}
                            size="small" 
                            variant="outlined" 
                          />
                        </TableCell>
                        <TableCell>
                          {infoEditing ? (
                            <TextField
                              fullWidth
                              size="small"
                              type={info.field_type === 'email' ? 'email' : 
                                    info.field_type === 'number' ? 'number' : 
                                    info.field_type === 'date' ? 'date' : 'text'}
                              value={info.value || ''}
                              onChange={(e) => handleInfoChange(index, e.target.value)}
                              placeholder={info.description || ''}
                              multiline={info.field_type === 'textarea'}
                              rows={info.field_type === 'textarea' ? 3 : 1}
                              required={info.is_required}
                            />
                          ) : (
                            info.value || <Typography color="text.secondary" component="span">Non renseigné</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {info.custom_position_index !== null ? info.custom_position_index : 'Non défini'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {infoEditing ? (
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={info.show_in_documents}
                                  onChange={(e) => {
                                    const updated = [...clientInfo];
                                    updated[index] = { ...updated[index], show_in_documents: e.target.checked };
                                    setClientInfo(updated);
                                  }}
                                />
                              }
                              label=""
                            />
                          ) : (
                            <Chip 
                              label={info.show_in_documents ? 'Oui' : 'Non'} 
                              size="small" 
                              color={info.show_in_documents ? 'success' : 'default'}
                            />
                          )}
                        </TableCell>
                        {infoEditing && (
                          <TableCell>
                            <Chip 
                              label={info.is_required ? 'Oui' : 'Non'} 
                              size="small" 
                              color={info.is_required ? 'error' : 'default'}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </TabPanel>

        {/* Checks Tab */}
        <TabPanel value={tabValue} index={4}>
          <TableContainer sx={{ px: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>N° de chèque</strong></TableCell>
                  <TableCell><strong>Direction</strong></TableCell>
                  <TableCell><strong>Banque</strong></TableCell>
                  <TableCell><strong>Montant</strong></TableCell>
                  <TableCell><strong>Statut</strong></TableCell>
                  <TableCell><strong>Date d'émission</strong></TableCell>
                  <TableCell><strong>Date d'échéance</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {clientChecks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">Aucun chèque</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  clientChecks.map((c) => (
                    <TableRow key={c.id} hover>
                      <TableCell sx={{ fontWeight: 500 }}>{c.check_number}</TableCell>
                      <TableCell>
                        <Chip
                          label={c.direction === 'incoming' ? 'Entrant' : 'Sortant'}
                          color={c.direction === 'incoming' ? 'success' : 'error'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{c.bank_name || '-'}</TableCell>
                      <TableCell>{c.amount?.toLocaleString?.()} {c.currency}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            c.status === 'draft' ? 'Brouillon' :
                            c.status === 'pending' ? 'En attente' :
                            c.status === 'cleared' ? 'Encaissé' :
                            c.status === 'bounced' ? 'Impayé' :
                            c.status === 'cancelled' ? 'Annulé' : c.status
                          }
                          color={
                            c.status === 'pending' ? 'warning' :
                            c.status === 'cleared' ? 'success' :
                            c.status === 'bounced' ? 'error' :
                            c.status === 'cancelled' ? 'default' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{c.issue_date}</TableCell>
                      <TableCell>{c.due_date || '-'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>
      </Card>

      {/* Password Reset Dialog */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
          Nouveau mot de passe
        </DialogTitle>
        <DialogContent>
          {newPassword && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.primary' }}>
                Communiquez ces identifiants au client:
              </Typography>
              <Paper 
                sx={{ 
                  p: 3, 
                  bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
                  border: 1,
                  borderColor: (theme) => theme.palette.mode === 'dark' ? 'grey.700' : 'grey.300',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>Utilisateur</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      sx={{ 
                        fontFamily: 'monospace', 
                        fontWeight: 700, 
                        fontSize: '1.1rem',
                        color: 'primary.main',
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      {newPassword.username}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(newPassword.username)} color="primary">
                      <CopyIcon />
                    </IconButton>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.primary' }}>Mot de passe</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography 
                      sx={{ 
                        fontFamily: 'monospace', 
                        fontWeight: 700, 
                        fontSize: '1.1rem',
                        color: 'success.main',
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? 'grey.900' : 'white',
                        px: 2,
                        py: 0.5,
                        borderRadius: 1,
                      }}
                    >
                      {newPassword.new_password}
                    </Typography>
                    <IconButton size="small" onClick={() => copyToClipboard(newPassword.new_password)} color="primary">
                      <CopyIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPasswordDialog(false)} variant="contained" size="large">Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Add Information Dialog */}
      <Dialog open={addInfoDialog} onClose={handleCloseAddInfoDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontSize: '1.5rem', fontWeight: 600 }}>
          Ajouter une information
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {availableInfoTypes.length === 0 ? (
              <Alert severity="info">
                Tous les types d'informations disponibles ont déjà été ajoutés pour ce client.
              </Alert>
            ) : (
              <>
                <FormControl fullWidth>
                  <InputLabel id="info-type-label">Type d'information</InputLabel>
                  <Select
                    labelId="info-type-label"
                    value={selectedInfoType}
                    label="Type d'information"
                    onChange={(e) => setSelectedInfoType(e.target.value)}
                  >
                    {availableInfoTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Valeur"
                  value={newInfoValue}
                  onChange={(e) => setNewInfoValue(e.target.value)}
                  type={getSelectedTypeFieldType() === 'email' ? 'email' :
                       getSelectedTypeFieldType() === 'number' ? 'number' :
                       getSelectedTypeFieldType() === 'date' ? 'date' : 'text'}
                  placeholder={availableInfoTypes.find(t => t.id === parseInt(selectedInfoType))?.description || ''}
                  multiline={getSelectedTypeFieldType() === 'textarea'}
                  rows={getSelectedTypeFieldType() === 'textarea' ? 3 : 1}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={showInDocuments}
                      onChange={(e) => setShowInDocuments(e.target.checked)}
                    />
                  }
                  label="Afficher dans les documents"
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseAddInfoDialog} variant="outlined">
            Annuler
          </Button>
          {availableInfoTypes.length > 0 && (
            <Button
              onClick={handleSaveNewInfo}
              variant="contained"
              disabled={!selectedInfoType || addInfoSaving}
              startIcon={addInfoSaving ? <CircularProgress size={16} /> : null}
            >
              Ajouter
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} message={snackbar.message} />

      {/* Payment Modal */}
      {paymentModal.document && (
        <PaymentModal
          open={paymentModal.open}
          onClose={() => setPaymentModal({ open: false, document: null })}
          document={paymentModal.document}
          onPaymentAdded={() => {
            fetchProfile();
            setSnackbar({ open: true, message: 'Paiement enregistré!' });
          }}
        />
      )}
    </Box>
  );
};

export default ClientProfilePage;
